/* One message as an element, from the page's `message-template`: headers,
 * actions, attachments, and the body inside a SHADOW ROOT so the email's own
 * CSS cannot restyle the page. The same body renderer is used for printing
 * (step 5), so what prints is what was on screen.
 */

import { sanitize } from "./sanitize.js";
import { markMatches, setMarkedText } from "./highlight.js";
import { longDate, person, size } from "./format.js";

/** The body's own stylesheet, inside the shadow root. Email assumes a white page. */
export const BODY_CSS = `
  :host { all: initial; display: block; color: #111; font: 15px/1.55 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; overflow-wrap: anywhere; }
  .plain { white-space: pre-wrap; max-width: 75ch; }
  .mail-body { min-width: 0; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #1f4f82; }
  mark { background: #d9f99d; color: inherit; border-radius: 3px; box-shadow: 0 0 0 1px #d9f99d; }
  .blocked-img {
    display: inline-flex; align-items: center; justify-content: center; max-width: 100%;
    min-height: 2.2em; padding: 6px 10px; box-sizing: border-box; vertical-align: middle;
    background: #f1efe9; border: 1px dashed #c9c4b8; color: #6b665d; text-align: center;
    font: 13px/1.3 ui-sans-serif, system-ui, -apple-system, sans-serif;
  }
  blockquote { margin: 0 0 0 0.6em; padding-left: 0.8em; border-left: 3px solid #d8d4cc; color: #444; }
`;

/**
 * Blob URLs for every part with a Content-ID, so `cid:` references resolve
 * to bytes already in the tab. The caller revokes `urls` when the view goes.
 */
export function contentIdUrls(parsed, urls) {
  const cids = new Map();
  for (const attachment of parsed.attachments || []) {
    if (!attachment.contentId) continue;
    const url = URL.createObjectURL(new Blob([attachment.content], { type: attachment.mimeType || "application/octet-stream" }));
    urls.push(url);
    cids.set(attachment.contentId.replace(/^<|>$/g, "").trim().toLowerCase(), url);
  }
  return cids;
}

/**
 * The parts a reader would call attachments: everything except images the
 * HTML body already shows through a `cid:` reference.
 */
export function realAttachments(parsed) {
  const html = (parsed.html || "").toLowerCase();
  return (parsed.attachments || []).filter((attachment) => {
    const id = (attachment.contentId || "").replace(/^<|>$/g, "").trim().toLowerCase();
    return !(id && html.includes(`cid:${id}`));
  });
}

/** Fills a shadow root with the message body. Returns how many remote things were blocked. */
export function renderBody(host, parsed, cids, options = {}) {
  const root = host.shadowRoot ?? host.attachShadow({ mode: "open" });

  if (parsed.html) {
    const clean = sanitize(parsed.html, cids, options);
    root.innerHTML = `<style>${BODY_CSS}</style>${clean.styles}<div class="mail-html"><div class="mail-body">${clean.body}</div></div>`;
    return clean.blocked;
  }

  root.innerHTML = `<style>${BODY_CSS}</style>`;
  const text = document.createElement("div");
  text.className = "plain";
  text.textContent = parsed.text || "";
  root.appendChild(text);
  return 0;
}

/** Header rows for a `<dl>`: From, To, Cc, Date. */
export function fillMeta(dl, parsed) {
  dl.replaceChildren();
  const row = (label, value) => {
    if (!value) return;
    const dt = document.createElement("dt");
    dt.className = "text-base-content/60";
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.className = "min-w-0 break-words";
    dd.textContent = value;
    dl.append(dt, dd);
  };

  row("From", person(parsed.from));
  row("To", (parsed.to || []).map(person).join(", "));
  row("Cc", (parsed.cc || []).map(person).join(", "));
  row("Date", parsed.date ? longDate(parsed.date) : "");
}

export function blockedText(count) {
  return `This email asked to load ${count === 1 ? "one picture or style" : `${count} pictures or styles`} from the internet. `
    + "Nothing was loaded, so the sender cannot tell you opened it.";
}

/**
 * The on screen message view.
 *
 * @param {HTMLTemplateElement} template  `message-template`
 * @param {HTMLTemplateElement} attachmentTemplate
 * @param {object} parsed    the decoded message
 * @param {string[]} urls    blob URLs made here, for the caller to revoke
 * @param {object} actions   pdf, eml, back, loadImages(always), blockImages(), setAlways(on)
 * @param {object} images    { load: whether to show this message's remote pictures,
 *                             always: the session setting, framePath }
 * @returns {{element: HTMLElement, framed: object|null}}
 */
export function messageView(template, attachmentTemplate, parsed, urls, actions, images = {}) {
  const view = template.content.firstElementChild.cloneNode(true);
  const find = (name) => view.querySelector(`[data-mb="${name}"]`);

  setMarkedText(find("msg-subject"), parsed.subject || "(no subject)", images.terms || []);
  fillMeta(find("msg-meta"), parsed);

  // Inside a conversation the subject and the way back belong to the
  // conversation, once, not to every message in it.
  if (images.compact) {
    find("msg-subject").remove();
    find("to-list").remove();
    view.classList.remove("lg:p-8", "sm:p-6", "p-4", "max-w-4xl");
    view.classList.add("p-4");
  }

  find("msg-pdf").addEventListener("click", () => actions.pdf(framed));
  find("msg-eml").addEventListener("click", actions.eml);
  find("to-list")?.addEventListener("click", actions.back);

  const list = find("msg-attachments");
  for (const attachment of realAttachments(parsed)) {
    const link = attachmentTemplate.content.firstElementChild.cloneNode(true);
    const url = URL.createObjectURL(new Blob([attachment.content], { type: attachment.mimeType || "application/octet-stream" }));
    urls.push(url);
    link.href = url;
    link.download = attachment.filename || "attachment";
    link.querySelector('[data-mb="attachment-name"]').textContent = attachment.filename || "Unnamed attachment";
    link.querySelector('[data-mb="attachment-size"]').textContent = size(attachment.content.byteLength);
    list.appendChild(link);
  }
  list.classList.toggle("hidden", !list.children.length);
  list.classList.toggle("flex", list.children.length > 0);

  // How much this message would fetch, counted without drawing it: once a
  // shadow root is attached, the frame could not be shown in its place.
  const blocked = parsed.html ? sanitize(parsed.html).blocked : 0;
  const body = find("msg-body");
  let framed = null;

  if (blocked && images.load && images.framePath) {
    framed = images.frame(body, parsed, images.framePath);
    find("msg-blocked-text").textContent = "Pictures from the internet are shown, so the sender can tell you opened this.";
    find("msg-load").classList.add("hidden");
    find("msg-unload").classList.remove("hidden");
  } else {
    renderBody(body, parsed, contentIdUrls(parsed, urls));
    // The search's words in the text. Not on paper: printing renders the body
    // afresh, so exported PDFs carry no marks.
    if (images.terms?.length) markMatches(body.shadowRoot, images.terms);
    find("msg-blocked-text").textContent = blocked ? blockedText(blocked) : "";
  }

  if (blocked) {
    find("msg-blocked").classList.replace("hidden", "flex");
    find("msg-always").checked = Boolean(images.always);
    find("msg-load").addEventListener("click", () => actions.loadImages());
    find("msg-unload").addEventListener("click", () => actions.blockImages());
    find("msg-always").addEventListener("change", (event) => actions.setAlways(event.target.checked));
  }

  return { element: view, framed };
}
